type IndexSignatureObject<T = unknown> = {
    [name: string]: T;
}

type ReferenceMap = WeakMap<object, {
    [key: string]: Element[];
}>;

type UpdateMap = WeakMap<object, ContentTarget[]>;

type IfEquals<X, Y, A = X, B = never> =
    (<T>() => T extends X ? 1 : 2) extends
    (<T>() => T extends Y ? 1 : 2) ? A : B;

type WritableKeys<T> = {
    [P in keyof T]-?: IfEquals<{ [Q in P]: T[P] }, { -readonly [Q in P]: T[P] }, P>
}[keyof T];

export type HTMLElementWritableProperties<
    T extends keyof HTMLElementTagNameMap> = Partial<Pick<
        HTMLElementTagNameMap[T], WritableKeys<HTMLElementTagNameMap[T]>>>;

export class Template<T extends {} = {}> {
    static document = document;
    static generateTypeScriptCode(element: Element, data: string) {
        return new CodeGenerator(element, data).generate();
    }
    private readonly referenceMap: ReferenceMap = new WeakMap;
    private readonly updateMap: UpdateMap = new WeakMap;
    private readonly updater: Updater;
    constructor(element: Element, private readonly root: Element, data: T) {
        const source = new HTMLElementSource(
            element, data, this.referenceMap, this.updateMap);
        const [node, ...targets] = source.expand(data);
        root.append(node);
        const [start, end] = source.surround(root);
        new RootContentTarget(source, data, start, end, targets);
        this.updater = new Updater(data, this.updateMap);
    }
    reference(object: {}, key: string): Element | undefined {
        return this.references(object, key).shift();
    }
    references(object: {}, key: string): Element[] {
        const reference = this.referenceMap.get(object);
        if (!reference)
            return [];
        const elements = reference[key];
        if (!elements)
            return [];
        reference[key] = elements.filter(
            element => this.root.contains(element));
        return reference[key]!.slice(0);
    }
    update(object?: object) {
        return this.updater.update(object);
    }
}

class Updater {
    private resolves: (() => void)[] = [];
    private readonly objects: object[] = [];
    constructor(
        private readonly data: {},
        private readonly updateMap: UpdateMap) {
    }
    update(object?: object) {
        if (!object)
            object = this.data;
        if (!this.objects.includes(object))
            this.objects.push(object);
        if (this.resolves.length == 0)
            requestAnimationFrame(() => {
                for (const object of this.objects.splice(0))
                    for (const target of this.updateMap.get(object)!)
                        target.updateValue(object);
                for (const resolve of this.resolves.splice(0))
                    resolve();
            });
        return new Promise<void>(resolve => {
            this.resolves.push(resolve);
        })
    }
}

abstract class Source<N extends Node> {
    readonly node: N;
    abstract readonly clonable: boolean;
    protected abstract createNode(node: N): N;
    constructor(node: N, protected readonly data: unknown) {
        this.node = this.createNode(node);
    }
    abstract expand(data: unknown): [Node, ...Target[]];
}

class ElementSource extends Source<Element> {
    readonly clonable: boolean = true;
    private readonly children: Source<Node>[] = [];
    private readonly attributes: IndexSignatureObject<string> = {};
    private readonly content?: Function;
    private readonly attribute?: Function;
    private readonly property?: Function;
    private readonly create?: Function;
    private readonly callback?: Function;
    private readonly reference: string | null;
    private readonly fragment: boolean;
    constructor(
        node: Element, data: unknown,
        protected readonly referenceMap: ReferenceMap,
        readonly updateMap: UpdateMap) {
        super(node, data);
        for (const { name, value } of node.attributes)
            this.attributes[name] = value;
        const directives = [
            'content', 'attribute', 'property', 'create', 'callback'] as const;
        for (const directive of directives) {
            if (this.attributes[`data-${directive}`])
                throw `data-${directive}`;
            const name = `data-c-${directive}`;
            delete this.attributes[name];
            const expression = node.getAttribute(name);
            if (expression != null) {
                try {
                    const evaluator = Function('data', `return ${expression};`);
                    this[directive] = (object: unknown) => {
                        try {
                            return evaluator.call(object, this.data);
                        } catch (e) {
                            if (e instanceof Error)
                                e.message += ` (${name}="${expression}")`;
                            throw e;
                        }
                    };
                } catch (e) {
                    if (e instanceof Error)
                        e.message += ` (${name}="${expression}")`;
                    throw e;
                }
                this.clonable = false;
            }
        }
        delete this.attributes['data-c-reference'];
        this.reference = node.getAttribute('data-c-reference');
        delete this.attributes['data-c-type'];
        for (const name of Object.keys(this.attributes))
            if (name.startsWith('data-c-'))
                throw new Error(name);
        for (const child of node.childNodes) {
            if (this.isIgnoredNode(child))
                continue;
            const source = this.createSource(child);
            this.children.push(source);
            if (source.clonable)
                this.node.append(source.node);
            else
                this.clonable = false;
        }
        if (this.clonable)
            for (const [name, value] of Object.entries(this.attributes))
                this.node.setAttribute(name, value);
        this.fragment = this.isFragment();
    }
    expand(object: {}): [node: Node, ...target: Target[]] {
        if (this.clonable)
            return [this.node.cloneNode(true)];
        if (!this.create)
            return this.expandElement(object);
        const create = this.retrieveCreate(object);
        const fragment = this.node.ownerDocument.createDocumentFragment();
        const targets: Target[] = [];
        if (create) {
            const [parent, ...children] = this.expandElement(object);
            fragment.append(parent);
            targets.push(...children);
        }
        const [start, end] = this.surround(fragment);
        return [
            fragment,
            new CreateTarget(this, object, create, start, end, targets)];
    }
    expandElement(object: {}): [parent: ParentNode, ...targets: Target[]] {
        const targets: Target[] = [];
        if (this.fragment) {
            const fragment = document.createDocumentFragment();
            targets.push(...this.appendChildren(fragment, object));
            return [fragment, ...targets];
        }
        const element = document.createElement(this.node.tagName.toLowerCase());
        if (this.reference != null) {
            if (!this.referenceMap.has(object))
                this.referenceMap.set(object, {});
            const reference = this.referenceMap.get(object)!;
            const elements = reference[this.reference] ||= [];
            elements.push(element);
        }
        const target = new AttributeTarget(this, object, element);
        target.update();
        if (this.attribute)
            targets.push(target);
        targets.push(...this.appendChildren(element, object));
        if (this.property) {
            const target = new PropertyTarget(this, object, element);
            target.update();
            targets.push(target);
        }
        if (this.callback) {
            const target = new CallbackTarget(this, object, element);
            target.update();
            targets.push(target);
        }
        return [element, ...targets];
    }
    setProperties(object: unknown, parent: Element) {
        const properties = this.retrieveProperty(object);
        const element: any = parent;
        for (const [name, value] of Object.entries(properties)) {
            if (name in element)
                element[name] = value;
            else
                throw new Error(name);
        }
    }
    expandContents(object: unknown):
        [fragment: DocumentFragment, ...targets: Target[]] {
        const contents = this.node.ownerDocument.createDocumentFragment();
        if (this.isArrayLike(object)) {
            const targets = Array.from(object).map(item => {
                const [fragment, ...targets] = this.expandContents(item);
                const [start, end] = this.surround(fragment);
                contents.append(fragment);
                const target = new ContentTarget(
                    this, object, item, start, end, targets);
                return target;
            });
            return [contents, ...targets];
        }
        if (this.children.every(source => source.clonable)) {
            if (object && object.toString == Object.prototype.toString) {
                throw new Error;
            }

            contents.append(object as string);
            return [contents];
        }
        const targets = this.children.flatMap(source => {
            const [element, ...targets] = source.expand(object);
            contents.append(element);
            return targets;
        });
        return [contents, ...targets];
    }
    isArrayLike(object: unknown): object is ArrayLike<any> {
        return Boolean(object && typeof object == 'object' &&
            'length' in object &&
            typeof (object as { length: unknown; }).length == 'number');
    }
    retrieveContent(object: unknown) {
        return this.content!.call(this, object);
    }
    retrieveAttribute(object: unknown) {
        const attributes: IndexSignatureObject = {};
        Object.assign(attributes, this.attributes);
        Object.assign(attributes, this.attribute?.call(this, object));
        return attributes;
    }
    retrieveProperty(object: unknown): IndexSignatureObject {
        return this.property!.call(this, object);
    }
    retrieveCallback(object: unknown): (element: Element) => void {
        return this.callback?.call(this, object);
    }
    retrieveCreate(object: unknown): boolean {
        return this.create!.call(this, object);
    }
    surround(parent: ParentNode): [Comment, Comment] {
        const document = this.node.ownerDocument;
        const start = document.createComment('');
        parent.prepend(start);
        const end = document.createComment('');
        parent.append(end);
        return [start, end];
    }
    protected createNode(node: Element): Element {
        return Template.document.createElement(node.tagName.toLowerCase());
    }
    protected createSource(node: Node): Source<Node> {
        if (node instanceof Element)
            return new ElementSource(
                node, this.data, this.referenceMap, this.updateMap);
        if (node instanceof CDATASection)
            return new CDATASectionSource(node, this.data);
        if (node instanceof Text)
            return new TextSource(node, this.data);
        if (node instanceof Comment)
            return new CommentSource(node, this.data);
        throw new Error;
    }
    protected isIgnoredNode(_node: Node) {
        return false;
    }
    private isFragment() {
        if (!['div', 'span'].includes(this.node.tagName.toLowerCase()) ||
            Object.keys(this.attributes).length > 0 ||
            this.attribute || this.property || this.callback ||
            this.reference != null)
            return false;
        if (this.create || this.content)
            return true;
        return false;
    }
    private appendChildren(parent: ParentNode, object: {}) {
        if (this.content) {
            const value = this.retrieveContent(object);
            const [contents, ...targets] = this.expandContents(value);
            const [start, end] = this.surround(contents);
            parent.append(contents);
            const target = new ContentTarget(
                this, object, value, start, end, targets);
            return [target];
        }
        const targets = this.children.flatMap(child => {
            const [node, ...targets] = child.expand(object);
            parent.append(node);
            return targets;
        });
        return targets;
    }
}

class HTMLElementSource extends ElementSource {
    protected override createSource(node: Node): Source<Node> {
        if (node instanceof Element)
            return new HTMLElementSource(
                node, this.data, this.referenceMap, this.updateMap);
        if (node instanceof CDATASection)
            return new TextSource(node, this.data);
        return super.createSource(node);
    }
    protected override isIgnoredNode(node: Node) {
        if (node instanceof Text && /^\s*$/.test(node.data))
            return true;
        if (node instanceof Comment)
            return true;
        return false;
    }
}

class TextSource extends Source<Text> {
    readonly clonable;
    private readonly evaluator?: Function;
    constructor(node: Text, data: unknown) {
        super(node, data);
        this.clonable = this.isClonable(node.data);
        this.clonable = true; // TODO: use template literal?
        if (this.clonable)
            return;
        const stmt = 'return String.raw`' + this.node.data + '`;';
        this.evaluator = Function('data', stmt);
    }
    expand(object: object): [Node, ...Target[]] {
        if (this.clonable)
            return [this.node.cloneNode()];
        const text = this.node.ownerDocument.createTextNode('');
        const target = new InterpolationTarget(this, object, text);
        target.update();
        return [text, target];
    }
    retrieveData(object: unknown) {
        return this.evaluator!.call(object, this.data);
    }
    protected createNode(node: Text): Text {
        return Template.document.createTextNode(node.data);
    }
    private isClonable(text: string) {
        const stmt = 'return (strings => strings.length)`' + text + '`;';
        try {
            return Function(stmt)() == 1;
        } catch (e) {
            if (e instanceof SyntaxError) {
                e.message += ` (${this.node.data})`;
                throw e;
            }
            return false;
        }
    }
}

class CDATASectionSource extends Source<CDATASection> {
    readonly clonable = true;
    expand(): [Node] {
        return [this.node.cloneNode()];
    }
    protected createNode(node: CDATASection): CDATASection {
        return Template.document.createCDATASection(node.data);
    }
}

class CommentSource extends Source<Comment> {
    readonly clonable = true;
    expand(): [Node] {
        return [this.node.cloneNode()];
    }
    protected createNode(node: Comment): Comment {
        return Template.document.createComment(node.data);
    }
}

abstract class Target<T extends Source<Node> = Source<Node>> {
    constructor(
        protected readonly source: T,
        protected readonly object: {}) {
    }
    abstract update(): void;
}

abstract class ElementTarget extends Target<ElementSource> { }

class ContentTarget extends ElementTarget {
    constructor(
        source: ElementSource, object: {}, private value: unknown,
        private start: Comment, private end: Comment,
        private readonly children: Target[]) {
        super(source, object);
        this.addToUpdateMap();
    }
    update() {
        const value = this.retrieveValue();
        if (Object.is(value, this.value))
            this.updateValue(value);
        else {
            const range = this.createRange();
            range.deleteContents();
            const [contents, ...targets] = this.source.expandContents(value);
            [this.start, this.end] = this.source.surround(contents);
            range.insertNode(contents);
            this.children.splice(0, this.children.length, ...targets);
            this.removeFromUpdateMap();
            this.value = value;
            this.addToUpdateMap();
        }
    }
    updateValue(value: unknown) {
        if (this.source.isArrayLike(value))
            this.updateArray(value);
        else
            for (const child of this.children)
                child.update();
    }
    protected retrieveValue() {
        return this.source.retrieveContent(this.object);
    }
    private addToUpdateMap() {
        if (!(this.value instanceof Object))
            return;
        if (!this.source.updateMap.has(this.value))
            this.source.updateMap.set(this.value, []);
        this.source.updateMap.get(this.value)!.push(this);
    }
    private removeFromUpdateMap() {
        if (!(this.value instanceof Object))
            return;
        const targets = this.source.updateMap.get(this.value)!;
        targets.splice(targets.indexOf(this), 1);
    }
    private updateArray(values: ArrayLike<unknown>) {
        const array: [unknown, ContentTarget, DocumentFragment][] = [];
        for (const child of this.children.splice(0) as ContentTarget[])
            array.push([
                child.value, child, child.createRange().extractContents()]);
        const range = this.createRange();
        range.deleteContents();
        const contents =
            this.source.node.ownerDocument.createDocumentFragment();
        for (const item of Array.from(values)) {
            const index = array.findIndex(([value]) => Object.is(value, item));
            if (index >= 0) {
                const tuple = array[index]!;
                tuple[1].updateValue(item);
                contents.append(tuple[2]);
                this.children.push(tuple[1]);
                array.splice(index, 1);
            } else {
                const [fragment, ...targets] = this.source.expandContents(item);
                const [start, end] = this.source.surround(fragment);
                contents.append(fragment);
                this.children.push(new ContentTarget(
                    this.source, values, item, start, end, targets));
            }
        }
        [this.start, this.end] = this.source.surround(contents);
        range.insertNode(contents);
        this.value = values;
    }
    private createRange() {
        const range = new Range;
        range.setStartBefore(this.start);
        range.setEndAfter(this.end);
        return range;
    }
}

class RootContentTarget extends ContentTarget {
    constructor(
        source: ElementSource, object: {},
        start: Comment, end: Comment,
        children: Target[]) {
        super(source, object, object, start, end, children);
    }
    protected override retrieveValue() {
        return this.object;
    }
}

class AttributeTarget extends ElementTarget {
    private attributes: IndexSignatureObject = {};
    private listener: EventListener = event => {
        const value = this.attributes['on' + event.type];
        const listener =
            typeof value == 'function' ? value : Function(String(value));
        listener.call(this.object, event);
    };
    constructor(
        source: ElementSource,
        object: {},
        private readonly element: Element) {
        super(source, object);
    }
    update() {
        const attributes = this.source.retrieveAttribute(this.object);
        const changes: typeof attributes = {};
        for (const [name, value] of Object.entries(attributes)) {
            if (name in this.attributes) {
                if (this.attributes[name] != value)
                    changes[name] = value;
                delete this.attributes[name];
            } else
                changes[name] = value;
        }
        for (const name of Object.keys(this.attributes))
            if (name.startsWith('on'))
                this.element.removeEventListener(
                    name.substring(2), this.listener);
            else
                this.element.removeAttribute(name);
        this.setAttributes(changes);
        this.attributes = attributes;
    }
    private setAttributes(attributes: IndexSignatureObject) {
        for (const [name, value] of Object.entries(attributes))
            if (value == undefined)
                this.element.removeAttribute(name);
            else if (name.startsWith('on'))
                this.attributes[name] ||
                    this.element.addEventListener(
                        name.substring(2), this.listener);
            else if (typeof value == 'boolean')
                this.element.toggleAttribute(name, value);
            else
                this.element.setAttribute(name, String(value));
    }
}

class PropertyTarget extends ElementTarget {
    constructor(
        source: ElementSource,
        object: {},
        private readonly element: Element) {
        super(source, object);
    }
    update() {
        this.source.setProperties(this.object, this.element);
    }
}

class CreateTarget extends ElementTarget {
    constructor(
        source: ElementSource, object: {}, private value: boolean,
        private readonly start: Comment, private readonly end: Comment,
        private children: readonly Target[]) {
        super(source, object);
    }
    update() {
        const value = this.source.retrieveCreate(this.object);
        if (value == this.value) {
            for (const child of this.children)
                child.update();
            return;
        }
        const range = new Range;
        range.setStartAfter(this.start);
        range.setEndBefore(this.end);
        range.deleteContents();
        this.children = [];
        if (value) {
            const [parent, ...targets] = this.source.expandElement(this.object);
            range.insertNode(parent);
            this.children = targets;
        }
        this.value = value;
    }
}

class CallbackTarget extends ElementTarget {
    constructor(
        source: ElementSource,
        object: {},
        private readonly element: Element) {
        super(source, object);
    }
    update() {
        const callback = this.source.retrieveCallback(this.object);
        callback.call(this.object, this.element);
    }
}

class InterpolationTarget extends Target<TextSource> {
    private data = this.text.data;
    constructor(source: TextSource, object: {}, private readonly text: Text) {
        super(source, object);
    }
    update() {
        const data = this.source.retrieveData(this.object);
        if (data == this.data)
            return;
        this.text.data = this.data = data;
    }
}

class CodeGenerator {
    private readonly element: CodeElement;
    counter = 0;
    constructor(element: Element, readonly data: string) {
        this.element = new CodeElement(element, this);
    }
    generate() {
        this.counter = 0;
        const statements = [`
import type { HTMLElementWritableProperties } from './template';
type Unarrayify<T> = T extends ArrayLike<infer E> ? Unarrayify<E> : T;
            `];
        statements.push(...this.element.generate(`Unarrayify<${this.data}>`));
        return statements.join('');
    }
}

abstract class CodeNode<T extends Node = Node> {
    readonly data = this.generator.data;
    readonly abstract leaf: boolean;
    readonly abstract counter?: number;
    constructor(
        protected readonly node: T,
        protected readonly generator: CodeGenerator) {
    }
    abstract generate(object: string): string[];
}

class CodeElement extends CodeNode<Element> {
    private readonly children: CodeNode<Node>[] = [];
    private readonly content?: ContentFunctionStatement;
    private readonly attribute?: AttributeFunctionStatement;
    private readonly property?: PropertyFunctionStatement;
    private readonly create?: CreateFunctionStatement;
    private readonly callback?: CallbackFunctionStatement;
    private readonly reference?: ReferenceFunctionStatement;
    private readonly statements: readonly FunctionStatement[];
    private readonly type;
    readonly leaf: boolean;
    readonly counter?: number;
    constructor(element: Element, generator: CodeGenerator) {
        super(element, generator);
        const content = element.getAttribute('data-c-content');
        if (content)
            this.content = new ContentFunctionStatement(this, content);
        const attribute = element.getAttribute('data-c-attribute');
        if (attribute)
            this.attribute = new AttributeFunctionStatement(this, attribute);
        const property = element.getAttribute('data-c-property');
        if (property)
            this.property = new PropertyFunctionStatement(
                this, property, element.tagName.toLowerCase());
        const create = element.getAttribute('data-c-create');
        if (create)
            this.create = new CreateFunctionStatement(this, create);
        const callback = element.getAttribute('data-c-callback');
        if (callback)
            this.callback = new CallbackFunctionStatement(
                this, callback, element.tagName.toLowerCase());
        const reference = element.getAttribute('data-c-reference');
        if (reference)
            this.reference = new ReferenceFunctionStatement(this);
        this.statements = [
            this.content, this.attribute, this.property, this.create,
            this.callback, this.reference].flat().filter(
                statement => statement) as FunctionStatement[];
        this.type = element.getAttribute('data-c-type');
        if (this.statements.length > 0 || this.type != null)
            this.counter = ++generator.counter;
        for (const child of element.childNodes)
            if (child instanceof Element)
                this.children.push(new CodeElement(child, generator));
            else if (child instanceof Text)
                this.children.push(new CodeText(child, generator));
        this.leaf = this.statements.length == 0 &&
            this.children.every(child => child.leaf);
    }
    generate(object: string) {
        const statements: string[] = [];
        const type = this.thistype(object);
        if (type)
            statements.push(`
type type${this.counter} = ${type};
`);
        statements.push(
            ...this.statements.map(statement => statement.generate()));
        const contents = this.content != null ?
            `Unarrayify<ReturnType<typeof content${this.counter}>>` :
            type || object;
        for (const child of this.children)
            if (!child.leaf)
                statements.push(...child.generate(contents));
        return statements;
    }
    private thistype(object: string) {
        if (this.statements.length == 0 && this.type == null)
            return;
        if (this.type)
            return `${this.type} extends ${object} ? ${this.type} :
    ${object} extends ${this.type} ? ${this.type} : never`;
        return object;
    }
}

class CodeText extends CodeNode<Text> {
    readonly leaf: boolean;
    readonly counter: number = 0;
    constructor(text: Text, generator: CodeGenerator) {
        super(text, generator);
        this.leaf = this.isClonable(text.data);
        if (!this.leaf)
            this.counter = ++generator.counter;
    }
    generate() {
        const statement =
            new InterpolationFunctionStatement(this, this.node.data);
        return [statement.generate()];
    }
    private isClonable(text: string) {
        const stmt = 'return (strings => strings.length)`' + text + '`;';
        try {
            return Function(stmt)() == 1;
        } catch (e) {
            if (e instanceof SyntaxError) {
                e.message += ` (${this.node.data})`;
                throw e;
            }
            return false;
        }
    }
}

abstract class FunctionStatement {
    protected readonly abstract name: string;
    constructor(
        protected readonly node: CodeNode,
        protected readonly expression: string) {
    }
    generate() {
        const f = this.functionName();
        return `
// @ts-ignore
function ${f}(this: type${this.node.counter}, data: ${this.node.data})${this.returnType()} {
    return ${this.expression};
}
`;
    }
    protected abstract returnType(): string;
    protected functionName() {
        return [this.name, this.node.counter].join('');
    }
}

class ContentFunctionStatement extends FunctionStatement {
    protected readonly name = 'content';
    protected returnType() {
        return '';
    }
}

class AttributeFunctionStatement extends FunctionStatement {
    protected readonly name = 'attribute';
    protected returnType() {
        return ': IndexSignatureObject';
    }
}

class PropertyFunctionStatement extends FunctionStatement {
    protected readonly name = 'property';
    constructor(
        element: CodeElement, expression: string,
        private readonly tagName: string) {
        super(element, expression);
    }
    protected returnType() {
        return `: HTMLElementWritableProperties<'${this.tagName}'>`;
    }
}

class CreateFunctionStatement extends FunctionStatement {
    protected readonly name = 'create';
    protected returnType() {
        return ': boolean';
    }
}

class CallbackFunctionStatement extends FunctionStatement {
    protected readonly name = 'callback';
    constructor(
        element: CodeElement, expression: string,
        private readonly tagName: string) {
        super(element, expression);
    }
    protected returnType() {
        return `: (element: HTMLElementTagNameMap[${this.tagName}]) => void `;
    }
}

class ReferenceFunctionStatement extends FunctionStatement {
    protected readonly name = 'reference';
    constructor(element: CodeElement) {
        super(element, 'new WeakMap().set(this, null)');
    }
    protected returnType() {
        return '';
    }
}

class InterpolationFunctionStatement extends FunctionStatement {
    protected readonly name = 'interpolation';
    constructor(text: CodeText, expression: string) {
        super(text, 'String.raw`' + expression + '`');
    }
    protected returnType() {
        return ': string';
    }
}
